import { useEffect, useState } from "react";
import PageHeader from "../../components/pageHeader";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";
import MainContentLayout from "../../components/mainContentLayout";
import AdminAttendanceTabs from "./adminAttendanceTabs";
import LoadingOverlay from "../../components/loadingOverlay";

export default function PlayerLeaveList() {
  const [players, setPlayers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterType, setFilterType] = useState("");
  const [filterPlayer, setFilterPlayer] = useState("");

  const { showToast } = useToast();
  const token = sessionStorage.getItem("access_token");

  const years = Array.from(
    { length: 6 },
    (_, i) => new Date().getFullYear() + i - 2
  );

  useEffect(() => {
    loadPlayers();
  }, []);

  useEffect(() => {
    loadLeaves();
  }, [selectedMonth, selectedYear, filterType, filterPlayer]);

  // Load coach's players
  async function loadPlayers() {
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/coach-players-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!data.success) {
        showToast("Failed to load players", "error");
        return;
      }

      setPlayers(data.players);
    } catch (err) {
      showToast("Error loading players", "error");
    } finally {
      setLoading(false);
    }
  }

  // Load leaves via backend filters
  async function loadLeaves() {
    setLoading(true);

    const params = new URLSearchParams({
      month: selectedMonth,
      year: selectedYear,
    });

    if (filterType) params.append("type", filterType);
    if (filterPlayer) params.append("playerId", filterPlayer);

    try {
      const res = await fetch(
        `${API_BASE}/api/coach/leaves?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (!data.success) {
        showToast("Failed to load leaves", "error");
        return;
      }

      const items = data.items.map((lv) => ({
        ...lv,
        startDate: new Date(lv.startDate),
        endDate: new Date(lv.endDate),
      }));

      setLeaves(items);
    } catch (err) {
      showToast("Error loading leaves", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} text="Loading..." />
      <PageHeader title="Players' Leave" backTo="/dashboard" />
      <AdminAttendanceTabs />

      {/* FILTERS */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-2 sm:space-y-0 mb-4 mt-4">

        {/* Month */}
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="border border-gray-400 rounded px-3 py-2 w-full sm:w-auto"
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <option key={i} value={i}>
              {new Date(0, i).toLocaleString("default", {
                month: "long",
              })}
            </option>
          ))}
        </select>

        {/* Year */}
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="border border-gray-400 rounded px-3 py-2 w-full sm:w-auto"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        {/* Type */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="border border-gray-400 rounded px-3 py-2 w-full sm:w-auto"
        >
          <option value="">All Types</option>
          <option value="Medical Leave">MC</option>
          <option value="Event Leave">Event</option>
          <option value="Tournament Leave">Tournament</option>
          <option value="Emergency Leave">Emergency</option>
        </select>

        {/* Player */}
        <select
          value={filterPlayer}
          onChange={(e) => setFilterPlayer(e.target.value)}
          className="border border-gray-400 rounded px-3 py-2 w-full sm:w-auto"
        >
          <option value="">All Players</option>
          {players.map((p) => (
            <option key={p.user_id} value={p.user_id}>
              {p.fullName}
            </option>
          ))}
        </select>
      </div>

      {/* LIST */}
      <ul className="divide-y">
        {leaves.length === 0 && (
          <li className="py-4 text-center text-gray-500">No leaves found</li>
        )}

        {leaves.map((lv, idx) => (
          <li
            key={lv.id}
            className="py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center"
          >
            <span className="font-medium text-left">
              {idx + 1}. {lv.fullName}
            </span>

            <div className="text-left sm:text-right mt-1 sm:mt-0">
              {lv.type === "Medical Leave" && (
                <span className="text-blue-600 font-semibold">MC</span>
              )}
              {lv.type === "Event Leave" && (
                <span className="text-green-600 font-semibold">Event</span>
              )}
              {lv.type === "Tournament Leave" && (
                <span className="text-purple-600 font-semibold">Tournament</span>
              )}
              {lv.type === "Emergency Leave" && (
                <span className="text-pink-600 font-semibold">
                  Emergency Leave
                </span>
              )}

              {lv.activityName && (
                <div className="text-sm text-gray-500">{lv.activityName}</div>
              )}

              {lv.emergencyReason && (
                <div className="text-sm text-gray-500">
                  Reason: {lv.emergencyReason}
                </div>
              )}

              {lv.remark && (
                <div className="text-sm text-gray-400 italic">
                  Remark: {lv.remark}
                </div>
              )}

              <div className="text-xs text-gray-500">
                {lv.startDate.toLocaleDateString()} –{" "}
                {lv.endDate.toLocaleDateString()}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </MainContentLayout>
  );
}
